import { Phone, Video } from 'lucide-react';
import { cn, formatCallDuration, formatRelativeDate } from '@/lib/utils';

interface CallRecordProps {
  message: {
    id: string;
    sender_id: string;
    created_at: string;
    call_kind?: string | null;
    call_status?: string | null;
    call_duration_seconds?: number | null;
  };
  /** True when the signed-in user placed the call. */
  mine: boolean;
}

function describeCall(
  kind: 'audio' | 'video',
  status: string,
  seconds: number,
  mine: boolean,
): { label: string; missed: boolean } {
  const spoken = kind === 'video' ? 'video call' : 'voice call';
  const label = kind === 'video' ? 'Video call' : 'Voice call';

  switch (status) {
    case 'missed':
      return mine
        ? { label: 'No answer', missed: false }
        : { label: `Missed ${spoken}`, missed: true };
    case 'cancelled':
      return mine
        ? { label: 'Cancelled call', missed: false }
        : { label: `Missed ${spoken}`, missed: true };
    case 'declined':
      return mine
        ? { label: 'Call declined', missed: false }
        : { label: `Declined ${spoken}`, missed: false };
    default:
      return {
        label: seconds > 0 ? `${label} · ${formatCallDuration(seconds)}` : label,
        missed: false,
      };
  }
}

/** Centred chip that stands in for a message bubble when a call happened. */
export function CallRecord({ message, mine }: CallRecordProps) {
  const kind = message.call_kind === 'video' ? 'video' : 'audio';
  const Icon = kind === 'video' ? Video : Phone;
  const { label, missed } = describeCall(
    kind,
    message.call_status || 'ended',
    Number(message.call_duration_seconds) || 0,
    mine,
  );

  return (
    <div className="flex justify-center py-1">
      <div
        className={cn(
          'flex max-w-[90%] items-center gap-2 rounded-full border px-3 py-1.5 text-xs',
          missed
            ? 'border-red-200 bg-red-50 text-red-600 dark:border-red-900/60 dark:bg-red-900/20 dark:text-red-400'
            : 'border-neutral-200 bg-neutral-50 text-neutral-600 dark:border-neutral-800 dark:bg-neutral-800/60 dark:text-neutral-300',
        )}
      >
        <Icon size={14} className="shrink-0" />
        <span className="truncate">{label}</span>
        <span className="shrink-0 text-[10px] text-neutral-400 dark:text-neutral-500">
          {formatRelativeDate(message.created_at)}
        </span>
      </div>
    </div>
  );
}
